import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTimeout } from '@hooks/useTimeout';
import useAmplitude from '@hooks/useAmplitude';
import Modal from '@components/ui/Modal';
import { getTextsConfirmProposal } from '@services/proposals';
import { TextConfirmProposalInterface } from '@interface/proposals';
import Loading from './Loading';
import Content from './Content';
import { ModalProposalConfirmPropsInterface } from './ModalProposalConfirmTypes';

function ModalProposalConfirm({
  isReject = false,
}: ModalProposalConfirmPropsInterface) {
  const [isLoading, setIsLoading] = useState(true);
  const [isShow, setIsShow] = useState(false);
  const iconNameRef = useRef('');
  const slugRef = useRef<TextConfirmProposalInterface['slug'] | ''>('');
  const titleRef = useRef('');
  const descriptionRef = useRef('');
  const ctaConfirmTextRef = useRef('');
  const ctaRejectTextRef = useRef('');
  const freightIdEncodedRef = useRef('');
  const truckerIdEncodedRef = useRef('');
  const addTimeout = useTimeout();
  const navigate = useNavigate();
  const { idProposal } = useParams();
  const { amplitudeEvent } = useAmplitude();
  const flow = isReject ? 'reject' : 'confirm';

  useEffect(() => {
    addTimeout(() => {
      setIsShow(true);
      amplitudeEvent(`modal_proposal_${flow}_show`);
    }, 200);

    getTextsConfirmProposal(idProposal as string, isReject)
      .then((response) => {
        titleRef.current = response.data.title;
        descriptionRef.current = response.data.description;
        ctaConfirmTextRef.current = response.data.buttons.confirm.text;
        ctaRejectTextRef.current = response.data.buttons.reject.text;
        iconNameRef.current = response.data.icon;
        slugRef.current = response.data.slug;
        freightIdEncodedRef.current = response.data.params.freightIdEncoded;
        truckerIdEncodedRef.current = response.data.params.truckerIdEncoded;
        setIsLoading(false);
      })
      .catch((error) => {
        window.dispatchEvent(
          new CustomEvent('proposalConfirmedError', {
            detail: {
              proposalId: idProposal,
              isReject,
              message: {
                title: 'Não foi possível realizar esta ação',
                description: 'Tente novamente mais tarde',
              },
              status: error?.status,
            },
          }),
        );
        handleClose();
      });
  }, []);

  function handleClose() {
    setIsShow(false);
    addTimeout(() => {
      navigate(-1);
    }, 350);
  }

  return (
    <Modal
      width="xs"
      onClose={() => handleClose()}
      showCloseButton
      hOverlay
      noMarginContent
      isOpen={isShow}
      zIndex={1050}
    >
      <Loading isShow={isLoading} />
      <Content
        onClose={() => handleClose()}
        icon={iconNameRef.current}
        isShow={!isLoading}
        title={titleRef.current}
        description={descriptionRef.current}
        actions={{
          confirm: { text: ctaConfirmTextRef.current },
          rejected: { text: ctaRejectTextRef.current },
        }}
        proposalId={idProposal as string}
        slug={slugRef.current}
        isReject={isReject}
        freightIdEncoded={freightIdEncodedRef.current}
        truckerIdEncoded={truckerIdEncodedRef.current}
      />
    </Modal>
  );
}

export default ModalProposalConfirm;
